import { Check, ChevronDown, ChevronsDownUp, ChevronsUpDown, Rows3, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ProductStatusCounts, ProductStatusFilter } from '@/types/product';
import type { GroupBy } from './productListView';

const TABS: Array<{ key: ProductStatusFilter; label: string; accent?: string }> = [
  { key: 'all', label: 'Alles' },
  { key: 'review', label: 'Nakijken', accent: 'text-[#B8600B]' },
  { key: 'draft', label: 'Concept' },
  { key: 'published', label: 'Gepubliceerd' },
  { key: 'archived', label: 'Archief' },
];

const GROUP_OPTIONS: Array<{ key: GroupBy; label: string }> = [
  { key: 'category', label: 'Categorie' },
  { key: 'status', label: 'Status' },
  { key: 'none', label: 'Niet groeperen' },
];

interface ProductToolbarProps {
  status: ProductStatusFilter;
  counts: ProductStatusCounts | null;
  onStatusChange: (status: ProductStatusFilter) => void;
  search: string;
  onSearchChange: (search: string) => void;
  categories: string[];
  category: string | null;
  onCategoryChange: (category: string | null) => void;
  labels: string[];
  selectedLabels: string[];
  onToggleLabel: (label: string) => void;
  groupBy: GroupBy;
  onGroupByChange: (groupBy: GroupBy) => void;
  allCollapsed: boolean;
  onToggleCollapseAll: () => void;
}

/**
 * Eén regel met alles wat de lijst bepaalt. De statustabs vervangen de losse
 * schakelaars van eerder: "uitgeschakelde tonen" en "archief tonen" waren twee
 * vragen om hetzelfde te bereiken, en samen konden ze een lege lijst opleveren
 * zonder dat je zag waarom.
 */
export function ProductToolbar({
  status,
  counts,
  onStatusChange,
  search,
  onSearchChange,
  categories,
  category,
  onCategoryChange,
  labels,
  selectedLabels,
  onToggleLabel,
  groupBy,
  onGroupByChange,
  allCollapsed,
  onToggleCollapseAll,
}: ProductToolbarProps) {
  const groupLabel = GROUP_OPTIONS.find((option) => option.key === groupBy)?.label ?? 'Categorie';

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-muted p-[3px]">
        {TABS.map((tab) => {
          const active = status === tab.key;
          const count = counts?.[tab.key];
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onStatusChange(tab.key)}
              className={cn(
                'flex h-7 items-center gap-1.5 rounded px-2.5 text-[13px] transition-colors',
                active
                  ? 'bg-card font-semibold text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
              {count !== undefined && (
                <span
                  className={cn(
                    'tabular-nums',
                    tab.accent && count > 0 ? `${tab.accent} font-semibold` : 'text-foreground/40',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="relative shrink-0">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="h-[34px] w-56 pl-8"
          placeholder="Zoek op naam"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger className="flex h-[34px] shrink-0 items-center gap-2 rounded-md border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span>Categorie</span>
          <span className="text-muted-foreground">{category ?? 'alle'}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
          <DropdownMenuItem onSelect={() => onCategoryChange(null)}>
            <Check className={cn('mr-2 h-4 w-4', category === null ? 'opacity-100' : 'opacity-0')} />
            Alle categorieën
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {categories.map((entry) => (
            <DropdownMenuItem key={entry} onSelect={() => onCategoryChange(entry)}>
              <Check
                className={cn('mr-2 h-4 w-4', category === entry ? 'opacity-100' : 'opacity-0')}
              />
              {entry}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger className="flex h-[34px] shrink-0 items-center gap-2 rounded-md border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span>Label</span>
          <span className="text-muted-foreground">
            {selectedLabels.length ? `${selectedLabels.length} gekozen` : 'alle'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Toont producten met minstens één gekozen label
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {labels.length === 0 && (
            <DropdownMenuItem disabled>Nog geen labels in gebruik</DropdownMenuItem>
          )}
          {labels.map((label) => (
            <DropdownMenuCheckboxItem
              key={label}
              checked={selectedLabels.includes(label)}
              onCheckedChange={() => onToggleLabel(label)}
              onSelect={(event) => event.preventDefault()}
            >
              {label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="ml-auto flex shrink-0 items-center gap-1 rounded-md bg-muted p-[3px]">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex h-7 items-center gap-2 rounded px-2.5 text-[13px] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Rows3 className="h-3.5 w-3.5" />
            <span>Groepeer: {groupLabel.toLowerCase()}</span>
            <ChevronDown className="h-3.5 w-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {GROUP_OPTIONS.map((option) => (
              <DropdownMenuItem key={option.key} onSelect={() => onGroupByChange(option.key)}>
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    groupBy === option.key ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {groupBy !== 'none' && (
          <>
            <div className="h-4 w-px bg-border" />
            <button
              type="button"
              onClick={onToggleCollapseAll}
              className="flex h-7 items-center gap-2 rounded bg-card px-2.5 text-[13px] font-semibold shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {allCollapsed ? (
                <ChevronsUpDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronsDownUp className="h-3.5 w-3.5" />
              )}
              <span>{allCollapsed ? 'Uitklappen' : 'Inklappen'}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ProductToolbar;
